/**
 * Clara 360 - Communication Service v2
 * Formal communication with cumulative outstanding amounts
 */

import { masterSupabase as supabase } from '../lib/supabase-master.js';
import whatsappApiService from './whatsappApiService';
import emailApiService from './emailApiService';

/**
 * Calculate dunning level based on outstanding months
 */
function calculateDunningLevel(outstandingMonths, daysOverdue) {
  // Multiple months = escalate faster
  if (outstandingMonths >= 3) return 3;
  if (outstandingMonths >= 2) return 2;
  
  // Single month by days
  if (daysOverdue <= 7) return 1;
  if (daysOverdue <= 14) return 2;
  return 3;
}

/**
 * Get all outstanding payments for a tenant
 */
async function getOutstandingPayments(tenantId) {
  try {
    const { data, error } = await supabase
      .from('rent_payments_with_status')
      .select('*')
      .eq('tenant_id', tenantId)
      .in('derived_state', ['open', 'overdue', 'partial'])
      .order('payment_year', { ascending: true })
      .order('payment_month', { ascending: true });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Error loading outstanding payments:', error);
    return [];
  }
}

/**
 * Format outstanding amounts list
 */
function formatOutstandingList(payments) {
  return payments.map(p => {
    const monthName = new Date(p.payment_year, p.payment_month - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const outstanding = p.expected_amount - p.received_amount;
    return `${monthName}: ${outstanding.toFixed(2)} €`;
  }).join('\n');
}

/**
 * Generate message text with variables
 */
function generateMessage(template, variables) {
  let message = template;
  Object.keys(variables).forEach(key => {
    message = message.replace(new RegExp(`{${key}}`, 'g'), variables[key]);
  });
  return message;
}

/**
 * Get formal dunning templates
 */
const TEMPLATES = {
  whatsapp: {
    1: `Sehr geehrte/r {tenant_salutation} {tenant_name},

wir möchten Sie höflich daran erinnern, dass folgende Mietzahlung(en) noch ausstehend:

{outstanding_list}

Gesamtrückstand: {total_amount} €

Bitte überweisen Sie den Betrag zeitnah auf unser Konto.

Mit freundlichen Grüßen
{owner_name}
{property_name}
Tel: {owner_phone}`,
    
    2: `Sehr geehrte/r {tenant_salutation} {tenant_name},

trotz unserer Zahlungserinnerung ist/sind folgende Mietzahlung(en) noch nicht eingegangen:

{outstanding_list}

Gesamtrückstand: {total_amount} €
Überfällig seit: {days} Tagen

Wir fordern Sie hiermit auf, den ausstehenden Betrag innerhalb von 7 Tagen zu begleichen.

Mit freundlichen Grüßen
{owner_name}
{property_name}
Tel: {owner_phone}`,
    
    3: `Sehr geehrte/r {tenant_salutation} {tenant_name},

LETZTE MAHNUNG

Folgende Mietzahlung(en) sind seit {days} Tagen überfällig:

{outstanding_list}

Gesamtrückstand: {total_amount} €

Sollte der Betrag nicht innerhalb von 3 Tagen auf unserem Konto eingehen, werden wir den Fall an unseren Rechtsanwalt übergeben.

Wir fordern Sie hiermit letztmalig zur sofortigen Zahlung auf.

Mit freundlichen Grüßen
{owner_name}
{property_name}
Tel: {owner_phone}`
  },
  
  email: {
    1: {
      subject: 'Zahlungserinnerung Miete - {property_name}',
      body: `Sehr geehrte/r {tenant_salutation} {tenant_name},

wir möchten Sie höflich daran erinnern, dass folgende Mietzahlung(en) noch ausstehend:

{outstanding_list}

Gesamtrückstand: {total_amount} €

Bitte überweisen Sie den Betrag zeitnah auf folgendes Konto:

[Bankverbindung]

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
{owner_name}
{property_name}
Tel: {owner_phone}
E-Mail: {owner_email}`
    },
    
    2: {
      subject: '2. Mahnung - Mietrückstand {property_name}',
      body: `Sehr geehrte/r {tenant_salutation} {tenant_name},

trotz unserer ersten Zahlungserinnerung ist/sind folgende Mietzahlung(en) noch nicht eingegangen:

{outstanding_list}

Gesamtrückstand: {total_amount} €
Überfällig seit: {days} Tagen

Wir fordern Sie hiermit förmlich auf, den ausstehenden Betrag innerhalb von 7 Tagen zu begleichen.

Bankverbindung:
[Bankverbindung]

Andernfalls behalten wir uns rechtliche Schritte vor.

Mit freundlichen Grüßen
{owner_name}
{property_name}
Tel: {owner_phone}
E-Mail: {owner_email}`
    },
    
    3: {
      subject: 'LETZTE MAHNUNG - Mietrückstand {property_name}',
      body: `Sehr geehrte/r {tenant_salutation} {tenant_name},

dies ist die letzte Mahnung vor Übergabe an unseren Rechtsanwalt.

Folgende Mietzahlung(en) sind seit {days} Tagen überfällig:

{outstanding_list}

Gesamtrückstand: {total_amount} €

Sollte der Betrag nicht innerhalb von 3 Tagen auf unserem Konto eingehen, werden wir ohne weitere Ankündigung rechtliche Schritte einleiten.

Bankverbindung:
[Bankverbindung]

Wir fordern Sie hiermit letztmalig zur sofortigen Zahlung auf.

Mit freundlichen Grüßen
{owner_name}
{property_name}
Tel: {owner_phone}
E-Mail: {owner_email}`
    }
  }
};

/**
 * Prepare communication data (with cumulative amounts)
 */
async function prepareCommData(paymentData, tenantData, propertyData, ownerData) {
  // Get all outstanding payments for this tenant
  const outstandingPayments = await getOutstandingPayments(tenantData.id);
  
  // Calculate totals
  const totalOutstanding = outstandingPayments.reduce((sum, p) => 
    sum + (p.expected_amount - p.received_amount), 0
  );
  
  // Calculate days overdue (from earliest payment)
  const earliestPayment = outstandingPayments[0];
  const dueDate = new Date(earliestPayment?.due_date || paymentData.due_date);
  const today = new Date();
  const daysOverdue = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
  
  // Calculate dunning level
  const dunningLevel = calculateDunningLevel(outstandingPayments.length, daysOverdue);
  
  // Format outstanding list
  const outstandingList = formatOutstandingList(outstandingPayments);
  
  // Determine salutation
  const salutation = tenantData.name.includes('Frau') ? 'Frau' : 
                     tenantData.name.includes('Herr') ? 'Herr' : '';
  
  return {
    dunningLevel,
    daysOverdue,
    totalOutstanding,
    outstandingList,
    outstandingCount: outstandingPayments.length,
    variables: {
      tenant_name: tenantData.name,
      tenant_salutation: salutation,
      outstanding_list: outstandingList,
      total_amount: totalOutstanding.toFixed(2),
      days: daysOverdue,
      owner_name: ownerData.name || 'Verwaltung',
      owner_phone: ownerData.phone || '',
      owner_email: ownerData.email || '',
      property_name: propertyData.property_name || propertyData.street
    }
  };
}

/**
 * Log communication event
 */
async function logEvent(data) {
  try {
    const { error } = await supabase
      .from('communication_events')
      .insert({
        rent_payment_id: data.rent_payment_id,
        tenant_id: data.tenant_id,
        property_id: data.property_id,
        channel: data.channel,
        dunning_level: data.dunning_level,
        template_used: data.template_used,
        subject: data.subject,
        message: data.message,
        payload: data.payload,
        external_ref: data.external_ref,
        status: data.status || 'sent'
      });
    
    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('❌ Error logging communication:', error);
    throw error;
  }
}

/**
 * Get communication history for a payment
 */
async function getHistory(rentPaymentId) {
  try {
    const { data, error } = await supabase
      .from('communication_events')
      .select('*')
      .eq('rent_payment_id', rentPaymentId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Error loading history:', error);
    throw error;
  }
}

/**
 * Send WhatsApp message
 */
async function sendWhatsApp(paymentData, tenantData, propertyData, ownerData, customMessage = null) {
  try {
    if (!tenantData.phone) {
      throw new Error('Keine Telefonnummer hinterlegt');
    }
    
    // Prepare data
    const commData = await prepareCommData(paymentData, tenantData, propertyData, ownerData);
    
    // Generate message (use custom or template)
    let message;
    if (customMessage) {
      message = generateMessage(customMessage, commData.variables);
    } else {
      const template = TEMPLATES.whatsapp[commData.dunningLevel];
      message = generateMessage(template, commData.variables);
    }
    
    // Send via WhatsApp API
    const result = await whatsappApiService.sendMessage(
      tenantData.phone,
      message,
      'text'
    );
    
    // Log event
    await logEvent({
      rent_payment_id: paymentData.id,
      tenant_id: tenantData.id,
      property_id: paymentData.property_id,
      channel: 'whatsapp',
      dunning_level: commData.dunningLevel,
      template_used: customMessage ? 'custom' : `whatsapp_level_${commData.dunningLevel}`,
      message: message,
      external_ref: result.messageId,
      status: result.success ? 'sent' : 'failed',
      payload: { 
        phone: tenantData.phone, 
        result,
        outstanding_count: commData.outstandingCount,
        total_amount: commData.totalOutstanding
      }
    });
    
    return { success: result.success, message: 'WhatsApp gesendet' };
  } catch (error) {
    console.error('❌ WhatsApp error:', error);
    throw error;
  }
}

/**
 * Send Email
 */
async function sendEmail(paymentData, tenantData, propertyData, ownerData, customSubject = null, customBody = null) {
  try {
    if (!tenantData.email) {
      throw new Error('Keine E-Mail-Adresse hinterlegt');
    }
    
    // Prepare data
    const commData = await prepareCommData(paymentData, tenantData, propertyData, ownerData);
    
    // Generate email (use custom or template)
    let subject, body;
    if (customSubject && customBody) {
      subject = generateMessage(customSubject, commData.variables);
      body = generateMessage(customBody, commData.variables);
    } else {
      const template = TEMPLATES.email[commData.dunningLevel];
      subject = generateMessage(template.subject, commData.variables);
      body = generateMessage(template.body, commData.variables);
    }
    
    // Send via Clara Email API
    const result = await emailApiService.sendEmail({
      to: tenantData.email,
      subject: subject,
      text: body,
      html: body.replace(/\n/g, '<br>'),
      context: {
        rent_payment_id: paymentData.id,
        tenant_id: tenantData.id,
        property_id: paymentData.property_id
      }
    });
    
    // Log event
    await logEvent({
      rent_payment_id: paymentData.id,
      tenant_id: tenantData.id,
      property_id: paymentData.property_id,
      channel: 'email',
      dunning_level: commData.dunningLevel,
      template_used: customSubject ? 'custom' : `email_level_${commData.dunningLevel}`,
      subject: subject,
      message: body,
      external_ref: result.messageId,
      status: result.success ? 'sent' : 'failed',
      payload: { 
        to: tenantData.email,
        outstanding_count: commData.outstandingCount,
        total_amount: commData.totalOutstanding,
        duration: result.duration
      }
    });
    
    return { 
      success: result.success, 
      message: 'E-Mail gesendet',
      messageId: result.messageId
    };
  } catch (error) {
    console.error('❌ Email error:', error);
    throw error;
  }
}

/**
 * Generate PDF
 */
async function generatePDF(paymentData, tenantData, propertyData, ownerData) {
  try {
    const commData = await prepareCommData(paymentData, tenantData, propertyData, ownerData);
    
    // TODO: PDF generation (backend needed)
    await logEvent({
      rent_payment_id: paymentData.id,
      tenant_id: tenantData.id,
      property_id: paymentData.property_id,
      channel: 'pdf',
      dunning_level: commData.dunningLevel,
      template_used: `pdf_level_${commData.dunningLevel}`,
      status: 'draft',
      payload: {
        outstanding_count: commData.outstandingCount,
        total_amount: commData.totalOutstanding
      }
    });
    
    return { 
      success: true, 
      message: 'PDF-Generierung folgt' 
    };
  } catch (error) {
    console.error('❌ PDF error:', error);
    throw error;
  }
}

/**
 * Add note
 */
async function addNote(paymentData, tenantData, noteText) {
  try {
    await logEvent({
      rent_payment_id: paymentData.id,
      tenant_id: tenantData.id,
      property_id: paymentData.property_id,
      channel: 'note',
      message: noteText,
      status: 'sent'
    });
    
    return { success: true, message: 'Notiz gespeichert' };
  } catch (error) {
    console.error('❌ Note error:', error);
    throw error;
  }
}

/**
 * Get template preview (for editing)
 */
async function getTemplatePreview(channel, paymentData, tenantData, propertyData, ownerData) {
  const commData = await prepareCommData(paymentData, tenantData, propertyData, ownerData);
  
  if (channel === 'whatsapp') {
    const template = TEMPLATES.whatsapp[commData.dunningLevel];
    return {
      dunningLevel: commData.dunningLevel,
      text: generateMessage(template, commData.variables),
      variables: commData.variables
    };
  } else if (channel === 'email') {
    const template = TEMPLATES.email[commData.dunningLevel];
    return {
      dunningLevel: commData.dunningLevel,
      subject: generateMessage(template.subject, commData.variables),
      body: generateMessage(template.body, commData.variables),
      variables: commData.variables
    };
  }
  
  return null;
}

/**
 * Update tenant contact information
 */
async function updateTenantContact(tenantId, contactData) {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .update(contactData)
      .eq('id', tenantId)
      .select()
      .single();
    
    if (error) throw error;
    
    console.log('✅ Tenant contact updated:', contactData);
    return data;
  } catch (error) {
    console.error('❌ Update tenant contact error:', error);
    throw error;
  }
}

export default {
  calculateDunningLevel,
  getOutstandingPayments,
  getHistory,
  sendWhatsApp,
  sendEmail,
  generatePDF,
  addNote,
  getTemplatePreview,
  updateTenantContact,
  TEMPLATES
};
